<template>
  <b-modal
    v-model="showModal"
    id="pricingRulesModal"
    :title="`Unit Pricing Rules - ${unit_name}`"
    modal-class="pricing-rules-modal"
    centered
    hide-footer
    size="lg"
  >
    <spinner-loader v-if="loading" :loading="loading" />
    <b-row class="mb-3" v-if="editable">
      <b-col class="text-left" sm="6">
        <b-button variant="secondary" @click="(e) => showPricingModal(null)">
          Add Rule
        </b-button>
      </b-col>
      <b-col sm="6">
        <b-input-group>
          <b-form-input v-model="search" placeholder="Search" />
        </b-input-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col class="pricing-rules-table-container">
        <b-table
          class="pricing-rules-table"
          hover
          striped
          :fields="fields"
          :items="rules"
          :filter="search"
          @row-clicked="onRowClicked"
        >
          <div
            class="table-description"
            slot="cell(descriptor)"
            slot-scope="data"
            v-html="data.value"
          />
          <template slot="cell(starts_at)" slot-scope="data">
            <span>
              {{ formatDate(data.item.starts_at) }}
            </span>
          </template>

          <template slot="cell(ends_at)" slot-scope="data">
            <span>
              {{ formatDate(data.item.ends_at) }}
            </span>
          </template>

          <template slot="cell(repeat_type)" slot-scope="data">
            <span>
              {{ getRepeatTypeName(data.item.repeat_type) }}
            </span>
          </template>

          <template slot="cell(repeat_on)" slot-scope="data">
            <span>
              {{
                getRepeatOnLabels(data.item.repeat_type, data.item.repeat_on)
              }}
            </span>
          </template>

          <template slot="cell(color)" slot-scope="data">
            <span
              class="rule-color-box"
              :style="`background: ${data.item.color || 'none'}`"
            />
          </template>

          <template slot="cell(priority)" slot-scope="data">
            <span>{{ getRateTypeName(data.item.priority) }}</span>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <pricing-modal ref="pricing-modal" @updated="() => onUpdated()" />

    <priority-modal ref="priority-modal" @updated="() => onUpdated()" />
  </b-modal>
</template>

<script>
import PricingMixin from "@/mixins/pricing-mixin";

export default {
  name: "PricingRulesModal",

  components: {
    PricingModal: () => import("@/components/modals/PricingModal"),
    PriorityModal: () => import("@/components/modals/PriorityModal"),
    SpinnerLoader: () => import("@/components/widgets/SpinnerLoader"),
  },

  mixins: [PricingMixin],

  data() {
    return {
      fields: [
        {
          key: "name",
          sortable: true,
          label: "Name",
        },
        {
          key: "starts_at",
          sortable: true,
          label: "Starts At",
        },
        {
          key: "ends_at",
          sortable: true,
          label: "Ends At",
        },
        {
          key: "rate",
          sortable: true,
          label: "Rate",
        },
        {
          key: "repeat_type",
          sortable: true,
          label: "Apply To",
        },
        {
          key: "repeat_on",
          sortable: true,
          label: "Occurs",
        },
        // {
        //   key: 'color',
        //   sortable: true,
        //   label: 'Color',
        // },
        {
          key: "priority",
          sortable: true,
          label: "Rate Type",
        },
      ],
      loading: false,
      search: "",
      showModal: false,
      rules: [],
      date: [],
      unit_id: 0,
      unit_name: "",
      editable: true,
    };
  },

  methods: {
    show(unit_id, date = null, editable = true) {
      this.unit_id = unit_id;
      this.date = date;
      this.editable = editable;
      this.rules = this.getPricingRules(unit_id, date);

      let unit = this.getUnits.find((unit) => unit.id === unit_id);

      this.unit_name = unit ? unit.unit_name : "";

      this.showModal = true;
    },

    hide() {
      this.showModal = false;
    },

    showPricingModal(rule = null) {
      let pricing_modal = this.$refs["pricing-modal"];

      if (pricing_modal) {
        pricing_modal.show(this.unit_id, rule);
      }
    },

    showPriorityModal(rule) {
      let priority_modal = this.$refs["priority-modal"];

      if (priority_modal) {
        priority_modal.show(rule);
      }
    },

    onRowClicked(item) {
      let rule = Object.assign({}, item);

      rule.starts_at = new Date(rule.starts_at);
      rule.ends_at = new Date(rule.ends_at);

      if (this.editable) {
        this.showPricingModal(rule);
      } else {
        this.showPriorityModal(rule);
      }
    },

    onUpdated() {
      this.rules = this.getPricingRules(this.unit_id, this.date);
      this.$emit("updated");
    },
  },
};
</script>

<style lang="scss">
.pricing-rules-modal {
  .modal-dialog {
    max-width: 1300px;
    width: 90%;
    margin: 1rem auto;

    @media (max-width: 768px) {
      width: 95%;
      margin: 0.5rem auto;
    }
  }

  .modal-body {
    max-height: 80vh;
    overflow-y: auto;
  }

  .pricing-rules-table-container {
    width: 100%;
    max-width: 100%;
    min-height: 300px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .pricing-rules-table {
    width: 100%;
    font-size: 14px;

    @media (max-width: 768px) {
      font-size: 12px;
    }

    th,
    td {
      white-space: nowrap;
      padding: 0.5rem;

      @media (max-width: 768px) {
        padding: 0.3rem;
      }
    }

    .rule-color-box {
      border-radius: 50%;
      display: block;
      margin: auto;
      width: 20px;
      height: 20px;

      @media (max-width: 768px) {
        width: 15px;
        height: 15px;
      }
    }
  }

  .mb-3 {
    @media (max-width: 576px) {
      flex-direction: column;
      gap: 0.5rem;

      .col {
        width: 100%;
        padding: 0;
      }

      .text-left {
        text-align: center !important;
      }
    }
  }
}
</style>
